import React from "react"
import { useForm, useWatch } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  Row,
  Col,
  Space,
  Button,
  Alert,
  Radio,
  Typography,
  message,
} from "@mimber/ui-web"
import {
  Form,
  InputField,
  SelectField,
  DatePickerField,
  InputNumberField,
  TimezoneField,
  RichTextEditorField,
  RadioGroupField,
  SwitchField,
} from "@mimber/forms"
import { AudienceSelectorField } from "@/components/fields"
import { useUpdateEvent } from "@mimber/client/mimber-organization"
import {
  UpdateEventDtoSchema,
  EventLocationType,
  AudienceTargetType,
  EventEditScope,
} from "@mimber/schema"
import type { UpdateEventDto } from "@mimber/schema"
import type { EditEventFormProps } from "./EditEventForm.types"
import { SCOPE_OPTIONS, STATUS_OPTIONS } from "./EditEventForm.utils"
import {
  LOCATION_TYPE_OPTIONS,
  VISIBILITY_OPTIONS,
} from "../CreateEventForm/CreateEventForm.utils"

const { Paragraph } = Typography

export const EditEventForm: React.FunctionComponent<EditEventFormProps> = ({
  event,
  onSuccess,
  onCancel,
}) => {
  const isInSeries = !!event.eventSeriesId

  const form = useForm<UpdateEventDto>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(UpdateEventDtoSchema) as any,
    mode: "all",
    defaultValues: {
      title: event.title,
      description: event.description ?? "",
      category: event.category ?? "",
      status: event.status,
      startDate: event.startDate,
      endDate: event.endDate,
      timezone: event.timezone,
      locationType: event.locationType,
      visibility: event.visibility,
      physicalAddress: event.physicalAddress ?? "",
      virtualLink: event.virtualLink ?? "",
      targetType: event.audienceTarget?.targetType ?? AudienceTargetType.ALL,
      tierTargetIds:
        event.audienceTarget?.tierTargets?.map((t) => t.membershipTier.uuid) ??
        [],
      groupTargetIds:
        event.audienceTarget?.groupTargets?.map((g) => g.group.uuid) ?? [],
      roleTargetIds:
        event.audienceTarget?.roleTargets?.map(
          (r) => r.organizationRole.uuid
        ) ?? [],
      memberTargetIds:
        event.audienceTarget?.memberTargets?.map((m) => m.membership.uuid) ??
        [],
      registrationCap: event.registrationCap ?? undefined,
      isPaidEvent: event.isPaidEvent,
      ...(isInSeries && { editScope: EventEditScope.THIS_EVENT }),
    },
  })

  const { control } = form
  const locationType = useWatch({ control, name: "locationType" })

  const mutation = useUpdateEvent(event.uuid, {
    onSuccess: () => {
      message.success("Event updated")
      onSuccess?.()
    },
    onError: (error) => {
      message.error(`Failed to update event: ${error.message}`)
    },
  })

  const onSubmit = (data: UpdateEventDto) => {
    mutation.mutate(data)
  }

  const showPhysicalAddress =
    locationType === EventLocationType.PHYSICAL ||
    locationType === EventLocationType.HYBRID
  const showVirtualLink =
    locationType === EventLocationType.VIRTUAL ||
    locationType === EventLocationType.HYBRID

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      disabled={mutation.isPending}
      layout="vertical"
    >
      {isInSeries && (
        <Alert
          type="info"
          showIcon
          message="This event is part of a recurring series"
          description={
            <RadioGroupField name="editScope" label="">
              <Space direction="vertical">
                {SCOPE_OPTIONS.map((opt) => (
                  <Radio key={opt.value} value={opt.value}>
                    <Space direction="vertical" size={0}>
                      <span>{opt.label}</span>
                      <Paragraph
                        type="secondary"
                        style={{ margin: 0, fontSize: 12 }}
                      >
                        {opt.description}
                      </Paragraph>
                    </Space>
                  </Radio>
                ))}
              </Space>
            </RadioGroupField>
          }
          style={{ marginBottom: 24 }}
        />
      )}

      <InputField
        name="title"
        label="Title"
        placeholder="Event title"
        required
      />

      <RichTextEditorField
        name="description"
        label="Description"
        placeholder="Describe the event..."
      />

      <InputField
        name="category"
        label="Category"
        placeholder="e.g., Social, Meeting, Workshop"
      />

      <SelectField
        name="status"
        label="Status"
        placeholder="Select status"
        required
        options={STATUS_OPTIONS}
      />

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <DatePickerField
            name="startDate"
            label="Start Date & Time"
            required
            showTime
          />
        </Col>
        <Col xs={24} sm={12}>
          <DatePickerField
            name="endDate"
            label="End Date & Time"
            required
            showTime
          />
        </Col>
      </Row>

      <TimezoneField name="timezone" label="Timezone" required />

      <SelectField
        name="locationType"
        label="Location Type"
        placeholder="Select location type"
        required
        options={LOCATION_TYPE_OPTIONS}
      />

      <SelectField
        name="visibility"
        label="Visibility"
        placeholder="Select visibility"
        required
        options={VISIBILITY_OPTIONS}
      />

      {showPhysicalAddress && (
        <InputField
          name="physicalAddress"
          label="Physical Address"
          placeholder="Enter the event address"
          required
        />
      )}

      {showVirtualLink && (
        <InputField
          name="virtualLink"
          label="Virtual Link"
          placeholder="https://zoom.us/..."
          required
        />
      )}

      <AudienceSelectorField
        control={control}
        label="Audience"
        disabled={mutation.isPending}
      />

      <InputNumberField
        name="registrationCap"
        label="Registration Cap"
        placeholder="Leave empty for unlimited"
        min={1}
      />

      <SwitchField
        name="isPaidEvent"
        label="Paid Event"
        description="Enable ticket tiers and paid registration for this event"
      />

      <Space style={{ marginTop: 16 }}>
        <Button type="primary" htmlType="submit" loading={mutation.isPending}>
          Save Changes
        </Button>
        {onCancel && <Button onClick={onCancel}>Cancel</Button>}
      </Space>
    </Form>
  )
}
